import {
  TChildrenContainerOptions,
  TContainerOptions,
} from "app/schema/paywallTemplate/element/containerOptions"
import { TBadgeElement } from "app/schema/paywallTemplate/element/elements/Badge"
import { TBulletElement, TBulletSettings } from "app/schema/paywallTemplate/element/elements/Bullet"
import { TButtonElement, TButtonSettings } from "app/schema/paywallTemplate/element/elements/Button"
import { TButtonBaseElement } from "app/schema/paywallTemplate/element/elements/ButtonBase"
import { TCardElement } from "app/schema/paywallTemplate/element/elements/Card"
import { TCloseModalElement } from "app/schema/paywallTemplate/element/elements/CloseModal"
import {
  TContainerElement,
  TContainerSettings,
} from "app/schema/paywallTemplate/element/elements/Container"
import { TDivElement, TDivSettings } from "app/schema/paywallTemplate/element/elements/Div"
import { TDividerElement } from "app/schema/paywallTemplate/element/elements/Divider"
import {
  TDividingHeaderElement,
  TDividingHeaderSettings,
} from "app/schema/paywallTemplate/element/elements/DividingHeader"
import { TDrawerElement, TDrawerSettings } from "app/schema/paywallTemplate/element/elements/Drawer"
import { TDropdownElement } from "app/schema/paywallTemplate/element/elements/Dropdown"
import { THTMLElement, THTMLSettings } from "app/schema/paywallTemplate/element/elements/HTML"
import {
  THeadingElement,
  THeadingSettings,
} from "app/schema/paywallTemplate/element/elements/Heading"
import { TImageElement, TImageSettings } from "app/schema/paywallTemplate/element/elements/Image"
import {
  TImageBaseElement,
  TImageBaseSettings,
} from "app/schema/paywallTemplate/element/elements/ImageBase"
import { TListElement, TListSettings } from "app/schema/paywallTemplate/element/elements/List"
import { TListV2Element, TListV2Settings } from "app/schema/paywallTemplate/element/elements/ListV2"
import { TLottieElement, TLottieSettings } from "app/schema/paywallTemplate/element/elements/Lottie"
import {
  TMarqueeElement,
  TMarqueeItemElement,
  TMarqueeItemSettings,
  TMarqueeSettings,
} from "app/schema/paywallTemplate/element/elements/Marquee"
import { TModalElement } from "app/schema/paywallTemplate/element/elements/Modal"
import {
  TParagraphElement,
  TParagraphSettings,
} from "app/schema/paywallTemplate/element/elements/Paragraph"
import {
  TProductsElement,
  TProductsSettings,
} from "app/schema/paywallTemplate/element/elements/Products"
import { TRatingElement, TRatingSettings } from "app/schema/paywallTemplate/element/elements/Rating"
import { TReviewElement, TReviewSettings } from "app/schema/paywallTemplate/element/elements/Review"
import { TSectionElement } from "app/schema/paywallTemplate/element/elements/Section"
import { TSubheadingElement } from "app/schema/paywallTemplate/element/elements/Subheading"
import {
  TSubtitleElement,
  TSubtitleSettings,
} from "app/schema/paywallTemplate/element/elements/Subtitle"
import { TTableElement, TTableSettings } from "app/schema/paywallTemplate/element/elements/Table"
import { TTextElement } from "app/schema/paywallTemplate/element/elements/Text"
import {
  TTimelineElement,
  TTimelineItemElement,
  TTimelineItemSettings,
} from "app/schema/paywallTemplate/element/elements/Timeline"
import { TTitleElement, TTitleSettings } from "app/schema/paywallTemplate/element/elements/Title"
import { TVideoElement, TVideoSettings } from "app/schema/paywallTemplate/element/elements/Video"

export const defaultContainerOptions: TContainerOptions = {
  backgroundColor: "",
  backgroundImage: "",
  backgroundOpacity: 1.0,
  badge: false,
  bleed: false,
  bleedBackground: false,
  bleedBottom: false,
  blur: false,
  card: false,
  cardRadius: "16px",
  cover: false,
  extraLightBleed: false,
  fullHeight: false,
  fullWidth: false,
  heavyPadding: false,
  height: "auto",
  inset: false,
  light: false,
  lightBleed: false,
  lightMargin: false,
  lightPadding: false,
  noBackground: false,
  noMargin: false,
  noPadding: false,
  noTopPadding: false,
  showDivider: false,
  style: "",
  textAlign: "left",
  textColor: "",
  width: "100%",
}

export const defaultChildrenContainer: TChildrenContainerOptions = {
  cover: false,
  noMargin: false,
  noBackground: false,
  width: "100%",
  fullWidth: false,
  height: "auto",
  fullHeight: false,
  style: "",
}

export const defaultBadgeComponent: Omit<TBadgeElement, "id"> = {
  name: "badge",
}

export const defaultBulletSettings: TBulletSettings = {
  bodyText: "This is a bullet body",
  centerContent: true,
  iconBackgroundColor: "var(--sw-brand-color)",
  iconBorderRadius: "5px",
  iconContainer: false,
  iconPadding: "calc(var(--sw-spacing) * 0.5)",
  iconPosition: "center",
  iconSize: "auto",
  iconURL: "logo.svg",
  showBody: true,
  showIcon: true,
  showTextIcon: false,
  showTitle: true,
  smallIcon: false,
  textIcon: "✅",
  textIconStyle: "",
  titleText: "This is a bullet title",
}

export const defaultBulletComponent: Omit<TBulletElement, "id"> = {
  name: "bullet",
  settings: defaultBulletSettings,
  container: defaultContainerOptions,
}

export const defaultButtonSettings: TButtonSettings = {
  text: "Button text",
  center: false,
  showLabel: true,
  borderRadius: 10,
  size: "regular",
  backgroundColor: "",
}

export const defaultButtonComponent: Omit<TButtonElement, "id"> = {
  name: "button",
  settings: defaultButtonSettings,
  container: defaultContainerOptions,
}

export const defaultButtonBaseComponent: Omit<TButtonBaseElement, "id"> = {
  name: "buttonBase",
}

export const defaultCardComponent: Omit<TCardElement, "id"> = {
  name: "card",
  container: { ...defaultContainerOptions, card: true },
  children: [],
}

export const defaultCloseModalComponent: Omit<TCloseModalElement, "id"> = {
  name: "closeModal",
  children: [],
  settings: {
    initialStyles: {},
  },
}

export const defaultContainerSettings: TContainerSettings = {
  backgroundColor: "",
  backgroundImage: "",
  backgroundOpacity: 1.0,
  badge: false,
  bleed: false,
  bleedBackground: false,
  bleedBottom: false,
  blur: false,
  card: false,
  cardRadius: "16px",
  cover: false,
  extraLightBleed: false,
  fullHeight: false,
  fullWidth: false,
  heavyPadding: false,
  height: "auto",
  inset: false,
  light: false,
  lightBleed: false,
  lightMargin: false,
  lightPadding: false,
  mediumMargin: false,
  noBackground: false,
  noMargin: false,
  noPadding: false,
  noTopPadding: false,
  showDivider: false,
  skipTag: false,
  style: "",
  textAlign: "left",
  textColor: "",
  width: "100%",
}

export const defaultContainerComponent: Omit<TContainerElement, "id"> = {
  name: "container",
  settings: defaultContainerSettings,
  children: [],
}

export const defaultDivSettings: TDivSettings = {
  style: "",
  initialStyles: {},
}

export const defaultDivComponent: Omit<TDivElement, "id"> = {
  name: "div",
  settings: defaultDivSettings,
  children: [],
}

export const defaultDividerComponent: Omit<TDividerElement, "id"> = {
  name: "divider",
}

export const defaultDividingHeaderSettings: TDividingHeaderSettings = {
  title: "Divider",
}

export const defaultDividingHeaderComponent: Omit<TDividingHeaderElement, "id"> = {
  name: "dividingHeader",
  settings: defaultDividingHeaderSettings,
}

export const defaultDrawerSettings: TDrawerSettings = {
  cardBackgroundBlur: true,
  containerRadius: "25px",
}

export const defaultDrawerComponent: Omit<TDrawerElement, "id"> = {
  name: "drawer",
  settings: defaultDrawerSettings,
  children: [],
}

export const defaultDropdownComponent: Omit<TDropdownElement, "id"> = {
  name: "dropdown",
  container: { ...defaultContainerOptions, card: true, lightPadding: true },
}

export const defaultHeadingSettings: THeadingSettings = {
  type: "h1",
  initialStyles: {},
}

export const defaultHeadingComponent: Omit<THeadingElement, "id"> = {
  name: "heading",
  settings: defaultHeadingSettings,
}

export const defaultHTMLSettings: THTMLSettings = {
  value: "",
}

export const defaultHTMLComponent: Omit<THTMLElement, "id"> = {
  name: "html",
  settings: defaultHTMLSettings,
}

export const defaultImageSettings: TImageSettings = {
  width: "100%",
  height: "auto",
  src: "https://static.superwallassets.com/qfOQD4mBY0vmxhkxJ9ckB",
  style: "",
}

export const defaultImageComponent: Omit<TImageElement, "id"> = {
  name: "image",
  settings: defaultImageSettings,
  container: defaultContainerOptions,
}

export const defaultImageBaseSettings: TImageBaseSettings = {
  altText: "Image",
  src: "https://static.superwallassets.com/qfOQD4mBY0vmxhkxJ9ckB",
  initialStyles: {
    width: "100%",
    height: "auto",
  },
}

export const defaultImageBaseComponent: Omit<TImageBaseElement, "id"> = {
  name: "imageBase",
  settings: defaultImageBaseSettings,
}

export const defaultListSettings: TListSettings = {
  horizontal: false,
  snap: false,
  hasItemPadding: true,
  fullHeight: false,
  height: "auto",
  indicatorBottomPadding: true,
  indicatorTopPadding: true,
  indicatorColor: "#0284ff",
  style: "",
}

export const defaultListComponent: Omit<TListElement, "id"> = {
  name: "list",
  settings: defaultListSettings,
  container: defaultContainerOptions,
  children: [],
  childrenContainer: defaultChildrenContainer,
}

export const defaultListV2Settings: TListV2Settings = {
  horizontal: false,
  snap: false,
  hasItemPadding: true,
  fullHeight: false,
  height: "auto",
  indicatorBottomPadding: true,
  indicatorTopPadding: true,
  indicatorColor: "#0284ff",
  style: "",
}

export const defaultListV2Component: Omit<TListV2Element, "id"> = {
  name: "listV2",
  settings: defaultListV2Settings,
  container: defaultContainerOptions,
  children: [],
  childrenContainer: defaultChildrenContainer,
}

export const defaultLottieSettings: TLottieSettings = {
  width: "100%",
  maxWidth: "100%",
  height: "auto",
  src: "",
  loop: true,
  style: "",
}

export const defaultLottieComponent: Omit<TLottieElement, "id"> = {
  name: "lottie",
  settings: defaultLottieSettings,
  container: defaultContainerOptions,
}

export const defaultMarqueeSettings: TMarqueeSettings = {
  reverse: false,
}

export const defaultMarqueeComponent: Omit<TMarqueeElement, "id"> = {
  name: "marquee",
  settings: defaultMarqueeSettings,
  container: defaultContainerOptions,
  children: [],
}

export const defaultMarqueeItemSettings: TMarqueeItemSettings = {
  src: "../public/assets/marquee/01.png",
  altText: "Marquee Item Image",
}

export const defaultMarqueeItemComponent: Omit<TMarqueeItemElement, "id"> = {
  name: "marqueeItem",
  settings: defaultMarqueeItemSettings,
}

export const defaultModalComponent: Omit<TModalElement, "id"> = {
  name: "modal",
  children: [],
}

export const defaultParagraphSettings: TParagraphSettings = {
  center: false,
  showEmoji: false,
  showTitle: true,
  showTitleRight: false,
  showCaption: true,
  showBody: true,
}

export const defaultParagraphComponent: Omit<TParagraphElement, "id"> = {
  name: "paragraph",
  settings: defaultParagraphSettings,
  container: defaultContainerOptions,
}

export const defaultProductsSettings: TProductsSettings = {
  productCount: "one",
  hideProducts: "auto",
  direction: "vertical",
  // footerPosition:
  selectedBorderWidth: "2px",
  selectedCheckBoxImage: "https://static.superwallassets.com/uoQsDwmrnamiqkfkzRKvC",
  borderWidth: "1px",
  borderColor: "rgb(70,70,70)",
  unselectedCheckBoxImage: "https://static.superwallassets.com/ElRGbHkiRQTvFQ3Gojbfu",
  checkMarkMargin: "0 12px 0 4px",
  reverseDirection: false,
  showProductCheckMark: "hide",
  primaryProductBadge: "show",
  secondaryProductBadge: "hide",
  tertiaryProductBadge: "hide",
}

export const defaultProductsComponent: Omit<TProductsElement, "id"> = {
  name: "products",
  settings: defaultProductsSettings,
}

export const defaultRatingSettings: TRatingSettings = {
  showCaption: true,
  captionText: "Based on 200,000 reviews",
}

export const defaultRatingComponent: Omit<TRatingElement, "id"> = {
  name: "rating",
  settings: defaultRatingSettings,
  container: defaultContainerOptions,
  stars: 5,
}

export const defaultReviewSettings: TReviewSettings = {
  stars: 5,
  minimal: false,
  bottomAuthor: false,
  style: "",
}

export const defaultReviewComponent: Omit<TReviewElement, "id"> = {
  name: "review",
  settings: defaultReviewSettings,
  container: defaultContainerOptions,
}

export const defaultSectionComponent: Omit<TSectionElement, "id"> = {
  name: "section",
  children: [],
}

export const defaultSubheadingComponent: Omit<TSubheadingElement, "id"> = {
  name: "subheading",
}

export const defaultSubtitleSettings: TSubtitleSettings = {
  text: "This is a subtitle",
  style: "",
}

export const defaultSubtitleComponent: Omit<TSubtitleElement, "id"> = {
  name: "subtitle",
  settings: defaultSubtitleSettings,
  container: defaultContainerOptions,
}

export const defaultTableSettings: TTableSettings = {
  altRowBackgroundColor: "var(--sw-foreground-color-7)",
  iconWidth: 20,
  rows: 7,
  blur: false,
}

export const defaultTableComponent: Omit<TTableElement, "id"> = {
  name: "table",
  settings: defaultTableSettings,
  container: defaultContainerOptions,
}

export const defaultTextComponent: Omit<TTextElement, "id"> = {
  name: "text",
  settings: {
    isParagraph: false,
    initialStyles: {},
    text: "",
  },
}

export const defaultTimelineItemSettings: TTimelineItemSettings = {
  bulletRadius: 10,
  checkmarkIcon: "mojo/check.svg",
  contentStyle: "",
  gutterWidth: "var(--sw-container-padding)",
  hideIcons: false,
  iconContainerShadow: "0px 0px 0px 0px rgba(0, 0, 0, 0.0)",
  iconContainerWidth: 34,
  iconUrl: "../public/assets/t-check.svg",
  iconWidth: 18,
  isLast: false,
  isStepCompleted: false,
  primaryColor: "var(--sw-brand-color)",
  secondaryColor: "var(--sw-brand-color)",
  showCheck: false,
  style: "",
  subtitleStyle: "",
  trailStyle: "",
}

export const defaultTimelineItemComponent: Omit<TTimelineItemElement, "id"> = {
  name: "timelineItem",
  settings: defaultTimelineItemSettings,
  container: defaultContainerOptions,
}

export const defaultTimelineComponent: Omit<TTimelineElement, "id"> = {
  name: "timeline",
  container: defaultContainerOptions,
  children: [],
}

export const defaultTitleSettings: TTitleSettings = {
  type: "h1",
  text: "This is a title",
  shadow: false,
  weight: "normal",
  badge: false,
  style: "",
}

export const defaultTitleComponent: Omit<TTitleElement, "id"> = {
  name: "title",
  container: defaultContainerOptions,
  settings: defaultTitleSettings,
}

export const defaultVideoSettings: TVideoSettings = {
  src: "../public/assets/video.mp4",
  paddingTop: "var(--sw-container-padding)",
  overlayColor: "rgba(0,0,0,0.0)",
  justifyContent: "flex-start",
  bleed: false,
  lightBleed: false,
  style: "",
  width: "",
  height: "",
  aspectSize: "contain",
}

export const defaultVideoComponent: Omit<TVideoElement, "id"> = {
  name: "video",
  container: defaultContainerOptions,
  settings: { ...defaultVideoSettings },
  children: [],
}

export const elementsWithChildren = [
  "card",
  "closeModal",
  "container",
  "div",
  "drawer",
  "list",
  "listV2",
  "marquee",
  "modal",
  "section",
  "timeline",
  "video",
]
